import React, { useState, useEffect } from 'react';
import api from '../../api';
import { cardStyle, CardDecorations, headingStyle, spinnerStyle } from './dashboardStyles';

interface RetrogradePlanet {
  planet: string;
  planetRu: string;
  startDate?: string;
  endDate?: string;
  startSignRu?: string;
  endSignRu?: string;
  isActive?: boolean;
}

interface RetrogradesData {
  retrogrades?: RetrogradePlanet[];
}

const PLANET_SYMBOLS: Record<string, string> = {
  Mercury: '☿', Venus: '♀', Mars: '♂', Jupiter: '♃',
  Saturn: '♄', Uranus: '♅', Neptune: '♆', Pluto: '♇',
};

const RETROGRADE_MEANINGS: Record<string, string> = {
  Mercury: 'Период пересмотра коммуникации, технологий и планов. Возможны задержки в поездках.',
  Venus: 'Время пересмотра отношений, ценностей и эстетики. Не покупайте дорогое.',
  Mars: 'Период пересмотра действий, энергии и амбиций. Возможны конфликты.',
  Jupiter: 'Время пересмотра убеждений, путешествий и расширения.',
  Saturn: 'Период пересмотра ответственности, структуры и дисциплины.',
  Uranus: 'Время пересмотра революционных изменений и свободы.',
  Neptune: 'Период пересмотра иллюзий, духовности и творчества.',
  Pluto: 'Время глубокой трансформации и перерождения.',
};

const DashboardRetrogrades: React.FC = () => {
  const [retrogrades, setRetrogrades] = useState<RetrogradePlanet[]>([]);
  const [loading, setLoading] = useState(true);
  const [hoveredPlanet, setHoveredPlanet] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchRetrogrades = async () => {
      try {
        console.log('[DashboardRetrogrades] Fetching retrogrades...');
        const res = await api.get<RetrogradesData>('/calculations/retrogrades');
        console.log('[DashboardRetrogrades] API response:', res);
        if (res.success && res.data) {
          const list = res.data.retrogrades || [];
          console.log('[DashboardRetrogrades] Retrogrades count:', list.length);
          setRetrogrades(list);
        } else {
          console.log('[DashboardRetrogrades] API returned success=false or no data');
        }
      } catch (error) {
        console.error('[DashboardRetrogrades] Error fetching retrogrades:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRetrogrades();
  }, []);

  if (loading) {
    return (
      <div style={{ ...cardStyle, padding: isMobile ? '16px' : '20px 24px', marginBottom: '32px' }}>
        <CardDecorations />
        <div style={{ ...spinnerStyle, padding: '16px' }}>🔄 Загрузка...</div>
      </div>
    );
  }

  return (
    <div style={{ ...cardStyle, padding: isMobile ? '16px' : '20px 24px', marginBottom: '32px' }}>
      <CardDecorations />
      <h2 style={{ ...headingStyle(isMobile), marginBottom: '12px' }}>↩️ Ретроградные планеты</h2>
      <div style={{ position: 'relative', zIndex: 1 }}>
        {retrogrades.length === 0 ? (
          <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>
            Нет ретроградных планет в данный момент
          </p>
        ) : (
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
          }}>
            {retrogrades.map((r) => {
              const meaning = RETROGRADE_MEANINGS[r.planet] || 'Период пересмотра и внутренней работы.';
              const symbol = PLANET_SYMBOLS[r.planet] || '✦';
              const isHovered = hoveredPlanet === r.planet;

              return (
                <div
                  key={r.planet}
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setHoveredPlanet(r.planet)}
                  onMouseLeave={() => setHoveredPlanet(null)}
                >
                  <div style={{
                    background: 'rgba(196, 160, 255, 0.08)',
                    border: '1px solid rgba(196, 160, 255, 0.25)',
                    borderRadius: '12px',
                    padding: '8px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    ...(isHovered ? {
                      borderColor: 'rgba(196, 160, 255, 0.5)',
                      background: 'rgba(196, 160, 255, 0.12)',
                    } : {}),
                  }}>
                    <span style={{ fontSize: '18px' }}>{symbol}</span>
                    <span style={{ color: '#c4a0ff', fontSize: '13px', fontWeight: 500 }}>
                      {r.planetRu || r.planet}
                    </span>
                    <span style={{
                      color: '#888',
                      fontSize: '11px',
                      background: 'rgba(255,255,255,0.05)',
                      padding: '2px 6px',
                      borderRadius: '6px',
                    }}>
                      ↩ Ретро
                    </span>
                  </div>

                  {/* Tooltip */}
                  {isHovered && !isMobile && (
                    <div style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 8px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'rgba(13, 20, 40, 0.95)',
                      border: '1px solid rgba(196, 160, 255, 0.3)',
                      borderRadius: '12px',
                      padding: '12px 16px',
                      width: '250px',
                      zIndex: 100,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                    }}>
                      <div style={{ color: '#c4a0ff', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>
                        {symbol} {r.planetRu || r.planet} — ретроградный
                      </div>
                      <div style={{ color: '#b0b0b0', fontSize: '12px', lineHeight: 1.5 }}>
                        {meaning}
                      </div>
                      {r.startDate && r.endDate && (
                        <div style={{ color: '#888', fontSize: '11px', marginTop: '8px' }}>
                          {new Date(r.startDate).toLocaleDateString('ru-RU')} — {new Date(r.endDate).toLocaleDateString('ru-RU')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardRetrogrades;
